// # Calendar Medium Component
// Extended week/month view with event display and navigation

'use client';

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import type { CalendarEvent, CalendarViewType, CalendarEventFormData } from '../../types/calendar';
import { CalendarEventCard } from './CalendarEventCard';
import { CalendarEventChip } from './CalendarEventChip';
import { CalendarCityView } from './CalendarCityView';
import { Calendar3DView } from './Calendar3DView';
import { CalendarTimelineView } from './CalendarTimelineView';
import { CalendarHeatmapView } from './CalendarHeatmapView';
import { CalendarDayDialog } from './CalendarDayDialog';
import { CalendarService } from '../../lib/calendar-service';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { useAuth } from '../../lib/auth';
import { getCurrentLanguage } from '../../lib/language-utils';
import { logDev } from '../../lib/log-dev';

interface CalendarMediumProps {
  events: CalendarEvent[];
  viewType?: CalendarViewType;
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  className?: string;
  showWeekends?: boolean;
  firstDayOfWeek?: number; // 0 = Sunday, 1 = Monday
  departments?: Array<{ id: string; name: string }>;
  userId?: string;
}

export function CalendarMedium({
  events,
  viewType = 'month',
  onEventClick,
  onDateClick,
  onCreateEvent,
  className = '',
  showWeekends = true,
  firstDayOfWeek = 1, // Monday
  departments = [],
  userId
}: CalendarMediumProps) {
  const { t, i18n } = useTranslation('calendar');
  const { user } = useAuth();
  const userLang = getCurrentLanguage(user?.language_preference);
  const locale = userLang === 'no' ? 'nb-NO' : 'en-US'; // Map language to locale
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState<Date | null>(null);
  const { isMobile } = useDeviceInfo();
  
  // # Debug: Log events when component receives them
  React.useEffect(() => {
    if (events && events.length > 0) {
      logDev.debug('[CalendarMedium] Received events:', {
        count: events.length,
        viewType,
        events: events.map(e => ({
          id: e.id,
          title: e.title,
          start_date: e.start_date,
          end_date: e.end_date,
          event_type: e.event_type
        }))
      });
    }
  }, [events, viewType]);

  // # Handle date click - open day dialog
  const handleDateClick = (date: Date) => {
    setDayDialogDate(date);
    setDayDialogOpen(true);
    onDateClick?.(date);
  };

  // # Handle event creation in dialog
  const handleCreateEventInDialog = async (date: Date, eventData?: CalendarEventFormData) => {
    if (!eventData) {
      // Just refresh if no event data (dialog closed)
      onCreateEvent?.(date);
      return;
    }
    
    try {
      const result = await CalendarService.createEvent(eventData);
      if (result.success) {
        // Reload events or notify parent
        onCreateEvent?.(date);
      }
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  // # Get days to display based on view type
  const getDaysToDisplay = (): Date[] => {
    const days: Date[] = [];
    
    if (viewType === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      // Start from the first day of the week containing the 1st of the month
      let startDate = new Date(firstDay);
      while (startDate.getDay() !== firstDayOfWeek) {
        startDate.setDate(startDate.getDate() - 1);
      }
      
      // Continue until we fill complete weeks after the last day of month
      let currentDay = new Date(startDate);
      while (currentDay <= lastDay || currentDay.getDay() !== firstDayOfWeek) {
        days.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }
    } else if (viewType === 'week') {
      // Get the week containing currentDate
      let startOfWeek = new Date(currentDate);
      while (startOfWeek.getDay() !== firstDayOfWeek) {
        startOfWeek.setDate(startOfWeek.getDate() - 1);
      }
      
      for (let i = 0; i < 7; i++) {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + i);
        days.push(day);
      }
    } else if (viewType === 'day') {
      days.push(new Date(currentDate));
    } else if (viewType === 'list') {
      // For list view, we'll show events from current month and next month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 2, 0); // Next month's last day
      
      let currentDay = new Date(firstDay);
      while (currentDay <= lastDay) {
        days.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }
    }
    
    return days;
  };

  // # Get events for a specific date
  const getEventsForDate = (date: Date): CalendarEvent[] => {
    if (!events || events.length === 0) return [];
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    
    const dateStr = formatDate(date);
    
    // Normalize event dates for comparison (extract date part if includes time)
    const normalizeEventDate = (dateStr: string): string => {
      if (!dateStr) return '';
      // Extract just the date part (YYYY-MM-DD) if it includes time
      if (dateStr.includes('T')) {
        return dateStr.split('T')[0];
      }
      // If already in YYYY-MM-DD format, return as is
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateStr;
      }
      // Try to parse and reformat
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch (e) {
        // If parsing fails, return original
      }
      return dateStr;
    };
    
    const matchingEvents = events.filter(event => {
      if (!event || !event.start_date || !event.end_date) return false;
      
      // Normalize both start and end dates
      const normalizedStart = normalizeEventDate(event.start_date);
      const normalizedEnd = normalizeEventDate(event.end_date);
      
      // Check if date falls within event range
      const matches = normalizedStart <= dateStr && normalizedEnd >= dateStr;
      
      // # Debug logging for date matching issues (only use when specifically debugging)
      // Commented out by default to prevent console spam - uncomment only when needed
      // if (matches) {
      //   logDev.debug('[CalendarMedium] Event matched for date:', {
      //     dateStr,
      //     eventId: event.id,
      //     eventTitle: event.title,
      //     normalizedStart,
      //     normalizedEnd,
      //     originalStart: event.start_date,
      //     originalEnd: event.end_date
      //   });
      // }
      
      return matches;
    });
    
    return matchingEvents;
  };

  // # Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // # Check if date is today
  const isToday = (date: Date): boolean => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    const today = new Date();
    today.setDate(today.getDate() - 1); // Subtract 1 day for correct date
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // # Check if date is in current month
  const isCurrentMonth = (date: Date): boolean => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    if (!currentDate || !(currentDate instanceof Date) || isNaN(currentDate.getTime())) {
      return false;
    }
    return (
      date.getMonth() === currentDate.getMonth() &&
      date.getFullYear() === currentDate.getFullYear()
    );
  };

  // # Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month' || viewType === 'list') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewType === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month' || viewType === 'list') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewType === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    today.setDate(today.getDate() - 1); // Subtract 1 day for correct date
    setCurrentDate(today);
  };

  // # Jump to first week with events
  const goToFirstEventWeek = () => {
    if (!events || events.length === 0) return;
    
    // # Find the earliest event
    const sortedEvents = [...events].sort((a, b) => {
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      return dateA.getTime() - dateB.getTime();
    });
    
    if (sortedEvents.length > 0) {
      const firstEventDate = new Date(sortedEvents[0].start_date);
      setCurrentDate(firstEventDate);
    }
  };

  // # Get current period label
  const getCurrentPeriodLabel = (): string => {
    if (viewType === 'month') {
      return currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    } else if (viewType === 'week') {
      const days = getDaysToDisplay();
      const start = days[0];
      const end = days[days.length - 1];
      return `${start.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (viewType === 'list') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const nextMonth = month + 1;
      const monthName = currentDate.toLocaleDateString(locale, { month: 'long' });
      const nextMonthName = new Date(year, nextMonth, 1).toLocaleDateString(locale, { month: 'long' });
      return `${monthName} - ${nextMonthName} ${year}`;
    } else {
      return currentDate.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  // # Get weekday names based on user's locale
  const getWeekdayNames = (): string[] => {
    const baseDate = new Date(2024, 0, 1); // Start with a Monday (Jan 1, 2024)
    const names: string[] = [];
    
    // Generate weekday names from locale
    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      names.push(date.toLocaleDateString(locale, { weekday: 'short' }));
    }
    
    const reordered = [...names.slice(firstDayOfWeek), ...names.slice(0, firstDayOfWeek)];
    return showWeekends ? reordered : reordered.filter((_, i) => i < 5);
  };

  const days = getDaysToDisplay();
  const weekdayNames = getWeekdayNames();

  // # Check if current week/period has any events
  const hasEventsInCurrentPeriod = days.some(day => {
    const dateEvents = getEventsForDate(day);
    return dateEvents && dateEvents.length > 0;
  });

  // # Render specialized views
  if (viewType === 'city') {
    return (
      <>
        <CalendarCityView
          events={events}
          onEventClick={onEventClick}
          onDateClick={handleDateClick}
          onCreateEvent={onCreateEvent}
          className={className}
          showWeekends={showWeekends}
          firstDayOfWeek={firstDayOfWeek}
        />
        <CalendarDayDialog
          isOpen={dayDialogOpen}
          onClose={() => setDayDialogOpen(false)}
          date={dayDialogDate}
          events={events}
          onEventClick={onEventClick}
          onCreateEvent={handleCreateEventInDialog}
          departments={departments}
        />
      </>
    );
  }

  if (viewType === '3d') {
    return (
      <>
        <Calendar3DView
          events={events}
          onEventClick={onEventClick}
          onDateClick={handleDateClick}
          onCreateEvent={onCreateEvent}
          className={className}
          showWeekends={showWeekends}
          firstDayOfWeek={firstDayOfWeek}
        />
        <CalendarDayDialog
          isOpen={dayDialogOpen}
          onClose={() => setDayDialogOpen(false)}
          date={dayDialogDate}
          events={events}
          onEventClick={onEventClick}
          onCreateEvent={handleCreateEventInDialog}
          departments={departments}
        />
      </>
    );
  }

  if (viewType === 'timeline') {
    return (
      <>
        <CalendarTimelineView
          events={events}
          onEventClick={onEventClick}
          onDateClick={handleDateClick}
          onCreateEvent={onCreateEvent}
          className={className}
        />
        <CalendarDayDialog
          isOpen={dayDialogOpen}
          onClose={() => setDayDialogOpen(false)}
          date={dayDialogDate}
          events={events}
          onEventClick={onEventClick}
          onCreateEvent={handleCreateEventInDialog}
          departments={departments}
        />
      </>
    );
  }

  if (viewType === 'heatmap') {
    return (
      <>
        <CalendarHeatmapView
          events={events}
          onEventClick={onEventClick}
          onDateClick={handleDateClick}
          onCreateEvent={onCreateEvent}
          className={className}
        />
        <CalendarDayDialog
          isOpen={dayDialogOpen}
          onClose={() => setDayDialogOpen(false)}
          date={dayDialogDate}
          events={events}
          onEventClick={onEventClick}
          onCreateEvent={handleCreateEventInDialog}
          departments={departments}
        />
      </>
    );
  }

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header with navigation */}
      <div className={`flex-shrink-0 border-b border-gray-100 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
            {getCurrentPeriodLabel()}
          </h2>
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-1.5'}`}>
            {!isMobile && (
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-all duration-200 flex items-center gap-1.5"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                <span>{t('today')}</span>
              </button>
            )}
            <div className={`flex items-center bg-gray-100 rounded-md ${isMobile ? 'gap-0 p-0.5' : 'gap-0.5 p-0.5'}`}>
              <button
                onClick={goToPrevious}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Previous"
              >
                <ChevronLeft className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
              <button
                onClick={goToNext}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Next"
              >
                <ChevronRight className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar grid */}
      <div className={`flex-1 flex flex-col ${isMobile ? 'p-1' : 'p-2'}`}>
        {viewType === 'list' ? (
          // List view - Show all events as a list
          <div className="flex-1 overflow-y-auto">
            {!events || events.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <p className="text-gray-500 text-lg mb-2">No events found</p>
                {onCreateEvent && (
                  <button
                    onClick={() => onCreateEvent(new Date())}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Create an event
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Group events by date */}
                {(() => {
                  // Filter out invalid events first
                  const validEvents = events.filter(event => 
                    event && 
                    event.id && 
                    event.title && 
                    event.start_date && 
                    event.end_date
                  );
                  
                  // Sort events by start date
                  const sortedEvents = validEvents.sort((a, b) => {
                    try {
                      const dateA = new Date(`${a.start_date}T${a.start_time || '00:00:00'}`);
                      const dateB = new Date(`${b.start_date}T${b.start_time || '00:00:00'}`);
                      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
                      return dateA.getTime() - dateB.getTime();
                    } catch {
                      return 0;
                    }
                  });

                  // Group by date
                  const eventsByDate = new Map<string, CalendarEvent[]>();
                  sortedEvents.forEach(event => {
                    const dateKey = event.start_date;
                    if (!eventsByDate.has(dateKey)) {
                      eventsByDate.set(dateKey, []);
                    }
                    eventsByDate.get(dateKey)!.push(event);
                  });

                  // Render grouped events
                  return Array.from(eventsByDate.entries()).map(([dateKey, dateEvents]) => {
                    let date: Date;
                    try {
                      date = new Date(dateKey);
                      if (isNaN(date.getTime())) {
                        date = new Date();
                      }
                    } catch {
                      date = new Date();
                    }
                    const isTodayDate = isToday(date);
                    const isPast = date < new Date() && !isTodayDate;

                    return (
                      <div key={dateKey} className="space-y-2">
                        {/* Date header */}
                        <div className={`sticky top-0 z-10 py-2 ${
                          isTodayDate ? 'bg-blue-50' : 'bg-gray-50'
                        } rounded-lg px-3 mb-2`}>
                          <h3 className={`font-semibold ${
                            isTodayDate ? 'text-blue-700' : 'text-gray-700'
                          } ${isMobile ? 'text-sm' : 'text-base'}`}>
                            {date.toLocaleDateString(locale, {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                            })}
                            {isTodayDate && (
                              <span className="ml-2 text-xs font-normal text-blue-600">({t('today')})</span>
                            )}
                          </h3>
                        </div>

                        {/* Events for this date */}
                        <div className="space-y-2 pl-2">
                          {dateEvents.map((event) => (
                            <CalendarEventChip
                              key={event.id}
                              event={event}
                              onClick={onEventClick}
                              compact={false}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        ) : viewType === 'month' || viewType === 'week' ? (
          <div className="flex-1 flex flex-col">
            {/* Weekday headers */}
            <div className={`grid grid-cols-7 flex-shrink-0 ${isMobile ? 'mb-0.5' : 'mb-1'}`}>
              {weekdayNames.map((day) => (
                <div
                  key={day}
                  className={`text-center font-semibold text-gray-500 uppercase ${
                    isMobile ? 'text-[8px] tracking-tight py-0.5' : 'text-[10px] tracking-wide py-1'
                  }`}
                >
                  {isMobile ? day.slice(0, 1) : day}
                </div>
              ))}
            </div>

            {/* Empty week message */}
            {viewType === 'week' && !hasEventsInCurrentPeriod && events && events.length > 0 && (
              <div className="flex items-center justify-center py-8 mb-4">
                <div className="text-center">
                  <p className="text-gray-500 mb-3 text-sm">No events this week</p>
                  <button
                    onClick={goToFirstEventWeek}
                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm hover:underline transition-all"
                  >
                    Go to first event →
                  </button>
                </div>
              </div>
            )}

            {/* Calendar days */}
            <div className={`flex-1 grid grid-cols-7 ${
              viewType === 'week' 
                ? 'grid-rows-1' 
                : 'grid-rows-5 auto-rows-fr'
            } ${isMobile ? 'gap-0.5' : 'gap-1'}`}>
              {days.map((date, index) => {
                if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
                  return null;
                }
                
                const dateEvents = getEventsForDate(date);
                const isTodayDate = isToday(date);
                const isCurrentMonthDate = isCurrentMonth(date);
                const isSelected = selectedDate && formatDate(selectedDate) === formatDate(date);

                // # Add subtle color based on day of week for medium/large views
                const dayOfWeek = date.getDay();
                const getDayColor = () => {
                  if (isTodayDate) return 'bg-blue-50 border-blue-200/50 shadow-sm';
                  if (isSelected) return 'bg-blue-50/50 border-blue-100/50';
                  if (!isCurrentMonthDate) return 'bg-gray-50/50 border-gray-50/30 hover:bg-gray-100/50';
                  
                  // Subtle colors for different days of the week - very light borders
                  const dayColors = {
                    0: 'bg-red-50/30 border-red-50/20', // Sunday - very subtle red
                    1: 'bg-white border-gray-50/40', // Monday - default white
                    2: 'bg-blue-50/20 border-blue-50/20', // Tuesday - very subtle blue
                    3: 'bg-white border-gray-50/40', // Wednesday - default white
                    4: 'bg-green-50/20 border-green-50/20', // Thursday - very subtle green
                    5: 'bg-white border-gray-50/40', // Friday - default white
                    6: 'bg-purple-50/20 border-purple-50/20', // Saturday - very subtle purple
                  };
                  
                  return dayColors[dayOfWeek as keyof typeof dayColors] || 'bg-white border-gray-50/40';
                };

                return (
                  <div
                    key={`${date.toISOString()}-${index}`}
                    className={`border rounded cursor-pointer transition-all duration-200 ${
                      isMobile ? 'p-1' : 'p-1.5'
                    } ${getDayColor()} hover:bg-opacity-80 hover:border-gray-100/50`}
                    onClick={() => {
                      setSelectedDate(date);
                      handleDateClick(date);
                    }}
                  >
                    {/* Day number and month */}
                    <div className={`flex items-center justify-between ${isMobile ? 'mb-0.5' : 'mb-1'}`}>
                      <div className="flex flex-col leading-none">
                        <span
                          className={`font-semibold ${
                            isMobile ? 'text-[10px]' : 'text-xs'
                          } ${
                            isTodayDate
                              ? `text-blue-700 bg-blue-200 rounded-full flex items-center justify-center ${
                                  isMobile ? 'w-4 h-4 text-[8px]' : 'w-5 h-5 text-[10px]'
                                }`
                              : isCurrentMonthDate
                              ? 'text-gray-900'
                              : 'text-gray-400'
                          }`}
                        >
                          {date.getDate()}
                        </span>
                        {/* Show month for first day of month or in week view */}
                        {(date.getDate() === 1 || viewType === 'week') && (
                          <span
                            className={`font-medium uppercase tracking-wide mt-0.5 ${
                              isMobile ? 'text-[7px]' : 'text-[9px]'
                            } ${
                              isTodayDate
                                ? 'text-blue-600'
                                : isCurrentMonthDate
                                ? 'text-gray-400'
                                : 'text-gray-300'
                            }`}
                          >
                            {date.toLocaleDateString(locale, { month: 'short' })}
                          </span>
                        )}
                      </div>
                      {dateEvents.length > 0 && !isMobile && (
                        <span className={`text-[10px] font-medium px-1 py-0.5 rounded ${
                          isTodayDate 
                            ? 'bg-blue-200 text-blue-700' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {dateEvents.length}
                        </span>
                      )}
                    </div>

                    {/* Events */}
                    <div className={isMobile ? 'space-y-0' : 'space-y-0.5'}>
                      {dateEvents.slice(0, isMobile ? 1 : 2).map((event) => (
                        <CalendarEventChip
                          key={event.id}
                          event={event}
                          onClick={(evt) => {
                            onEventClick?.(evt);
                          }}
                          compact={true}
                        />
                      ))}
                      {dateEvents.length > (isMobile ? 1 : 2) && (
                        <div className={`text-gray-500 font-medium ${isMobile ? 'text-[7px] px-0.5' : 'text-[8px] px-1'}`}>
                          +{dateEvents.length - (isMobile ? 1 : 2)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Day view
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {currentDate.toLocaleDateString(locale, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              {onCreateEvent && (
                <button
                  onClick={() => onCreateEvent(currentDate)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {t('addEvent')}
                </button>
              )}
            </div>

            {/* Events list for the day */}
            {getEventsForDate(currentDate).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>{t('noEventsScheduledForDay')}</p>
                {onCreateEvent && (
                  <button
                    onClick={() => onCreateEvent(currentDate)}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('createEvent')}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {getEventsForDate(currentDate).map((event) => (
                  <CalendarEventChip
                    key={event.id}
                    event={event}
                    onClick={onEventClick}
                    compact={false}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick add button for month/week view */}
      {(viewType === 'month' || viewType === 'week') && selectedDate && onCreateEvent && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => onCreateEvent(selectedDate)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Add event on {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </button>
        </div>
      )}

      {/* Day Dialog */}
      <CalendarDayDialog
        isOpen={dayDialogOpen}
        onClose={() => setDayDialogOpen(false)}
        date={dayDialogDate}
        events={events}
        onEventClick={onEventClick}
        onCreateEvent={handleCreateEventInDialog}
        departments={departments}
      />
    </div>
  );
}

export default CalendarMedium;

